// Pulls links out of a paste so the user never has to touch them.
//
// Copying an email into a plain <textarea> keeps only the visible text — "Schedule Your
// Interview" — and silently discards the href, which is usually the strongest evidence
// of a scam. But the clipboard carries a text/html flavor alongside the plain text, and
// that flavor still contains every href. Reading it lets the app harvest destinations
// automatically instead of asking the user to right-click a malicious link, which risks
// them clicking it by accident.
//
// Anchors are parsed with a regex rather than DOMParser so this stays dependency-free
// and unit-testable outside a browser.

const ANCHOR_RE = /<a\b[^>]*?href\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi
const BARE_URL_RE = /https?:\/\/[^\s<>"')\]]+/gi
const MAX_LINKS = 25

function decodeEntities(s) {
  return s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
}

function cleanText(raw) {
  return decodeEntities(String(raw || '').replace(/<[^>]*>/g, ''))
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120)
}

/**
 * @param {string} html  clipboardData.getData('text/html')
 * @param {string} plain clipboardData.getData('text/plain')
 * @returns {Array<{href: string, text: string}>}
 */
export function extractLinksFromClipboard(html, plain) {
  const links = []
  const seen = new Set()

  const add = (href, text) => {
    const clean = String(href || '').trim()
    // Only real web destinations. mailto:/tel:/javascript:/#anchors aren't scan targets.
    if (!/^https?:\/\//i.test(clean)) return
    const key = clean.toLowerCase()
    if (seen.has(key)) return
    if (links.length >= MAX_LINKS) return
    seen.add(key)
    links.push({ href: clean.replace(/["'<>]/g, ''), text: cleanText(text) })
  }

  // Anchors from the HTML flavor — this is where the hrefs survive
  let m
  ANCHOR_RE.lastIndex = 0
  while ((m = ANCHOR_RE.exec(String(html || ''))) !== null) {
    add(decodeEntities(m[1]), m[2])
  }

  // Bare URLs in the plain-text flavor: plain-text emails, or links the mail client
  // never wrapped in an anchor.
  for (const url of String(plain || '').match(BARE_URL_RE) || []) add(url, '')

  return links
}

/**
 * Serializes links for the scan context. Anchors are re-emitted as <a> tags so the
 * backend url-detector can compare display text against the real destination and
 * catch link masking; bare URLs are emitted plainly.
 */
export function buildLinkBlock(links) {
  const list = (links || []).filter(l => l && l.href)
  if (list.length === 0) return ''
  const lines = list.map(l => (l.text ? `<a href="${l.href}">${l.text}</a>` : l.href))
  return `LINKS IN MESSAGE:\n${lines.join('\n')}`
}

/** Merge newly found links into an existing list, preserving order, de-duped by href. */
export function mergeLinks(existing, found) {
  const out = [...(existing || [])]
  const seen = new Set(out.map(l => l.href.toLowerCase()))
  for (const l of found || []) {
    const key = l.href.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(l)
  }
  return out.slice(0, MAX_LINKS)
}
